import { Component } from '@theme/component';

/**
 * The live theme's Polaris configuration, read off echt.com.au's own
 * `new ReviewsWidget(...)` call on 5 Sep 2026 rather than transcribed from the
 * app block's settings — the app-block names (`show_avatar`, `show_location`)
 * are Shopify schema and mean nothing to this API, which is why an earlier
 * attempt at these produced a card with nothing on it but stars and text.
 *
 * This block is what makes the widget usable: the avatar, reviewer name and
 * location, the "Verified Customer" badge, the product name, replies, share,
 * and the "was this helpful?" votes, plus the summary header and the
 * search/sort/filter controls.
 */
/** Reviews.io's Polaris DOM, as targeted by the live theme's fix snippet. */
const REVIEW_ITEM = '.R-ContentList__item';
const REVIEW_STARS = '.R-RatingStars__stars';

/**
 * Reviews.io's public product API — the same endpoint the Polaris widget
 * queries once it boots; CORS-open, no key. Asking it ourselves BEFORE
 * mounting is the only way to learn whether the reviews it is about to show
 * actually belong to this product, because nothing server-side knows: the
 * `reviewscouk` count metafield is synced from the same wrong answer (Nova
 * Trek products, launched 3 Sep 2026 with zero reviews, carry a count of 1).
 *
 * WHAT IS ACTUALLY WRONG (measured 7 Sep 2026, `product/rating-batch`, which
 * returns the catalogue row a SKU matched and that row's `lookup` SKU group):
 *
 *   NOVAFLARELEGGINGS/PHANTOMBLACK/M → matched ARISEINVISHALTERBRA/BLACK/3X,
 *                                      lookup = FREEDOMSPORTSBRA/… (many roots)
 *   MODEPANT/BLACK/M                 → matched 30FLOWPANTS/BLACK/2X,
 *                                      lookup = 30FLOWPANTS + 33FLOWPANTS
 *   TECHJOGGERS/BLACK/M              → TECHJOGGERS (1 root)
 *   FLARERIBBEDV2/BLACK/M            → FLARERIBBED (1 root)
 *   echt-probe-none                  → [] (no catalogue row at all)
 *
 * The Nova entries were created in Reviews.io as copies of a Freedom Sportsbra
 * entry — the live PDP's Reviews.io structured_data for the Nova page still
 * carries the Freedom Sportsbra description — so they inherit that entry's
 * review group and serve a grab-bag of other products' reviews.
 *
 * An earlier version of this check compared the product's stats against a
 * nonsense "probe" SKU, on the theory that an unknown SKU returned a generic
 * fallback set. It does not: a nonsense SKU returns count 0, because Reviews.io
 * has no catalogue row for it, while Nova has one. That rule was measured
 * against the dev server and failed; this is its replacement.
 */
const API = 'https://api.reviews.io/product/review';
const TIMELINE_API = 'https://api.reviews.io/timeline/data';

/**
 * Reviews.io is a third party sitting on the path of a decision to HIDE
 * content. If it is slow or down we give up and behave exactly as this
 * component did before this check existed (mount, show), so an outage can
 * never blank real reviews.
 */
const TIMEOUT_MS = 3000;

/**
 * How many distinct product families a sample of 25 reviews may span before it
 * stops looking like one product's reviews. A genuine renamed/merged family
 * runs to 3 (MODEPANT: 33FLOWPANTS, 30FLOWPANTS, CONTRASTSHORT); the cloned
 * Nova entry spans 14.
 */
const MAX_ROOTS = 4;

/**
 * …unless one family dominates the sample anyway, which is what a real merged
 * family looks like when a stray review is attached to it. MODEPANT's top root
 * is 15/25 (60%); Nova's is 3/25 (12%).
 */
const MIN_DOMINANT_SHARE = 0.3;

/**
 * A review SKU is `ROOT/COLOUR/SIZE`. The root carries the product family, and
 * Reviews.io appends two- and three-letter variant suffixes to it
 * (FLARELEGGINGS, FLARELEGGINGS-TL, FLARELEGGINGS-PT are one family), so those
 * are stripped before comparing.
 *
 * @param {unknown} sku
 * @returns {string} '' when there is nothing usable.
 */
const rootOf = (sku) =>
  typeof sku === 'string' ? sku.split('/')[0].trim().toUpperCase().replace(/-[A-Z]{2,3}$/, '') : '';

const LIVE_OPTIONS = {
  types: 'product_review',
  lang: 'en',
  layout: '',
  store_review: { hide_if_no_results: false },
  third_party_review: { hide_if_no_results: false },
  questions: { hide_if_no_results: false, enable_ask_question: true, show_dates: true },
  header: {
    // The native summary includes hidden reviews. Our summary uses the
    // eligible rating buckets and shares its numbers with the PDP star row.
    enable_summary: false,
    enable_ratings: true,
    enable_attributes: false,
    enable_image_gallery: false,
    enable_percent_recommended: false,
    enable_write_review: true,
    enable_ask_question: true,
    enable_sub_header: true,
    rating_decimal_places: 1,
    use_write_review_button: false,
  },
  filtering: {
    enable: true,
    enable_text_search: true,
    enable_sorting: true,
    enable_product_filter: false,
    enable_media_filter: true,
    enable_overall_rating_filter: false,
    enable_language_filter: false,
    enable_language_filter_language_change: false,
    enable_ratings_filters: true,
    enable_attributes_filters: true,
    enable_expanded_filters: false,
  },
  reviews: {
    enable_avatar: true,
    enable_reviewer_name: true,
    enable_reviewer_address: true,
    reviewer_address_format: 'city, country',
    enable_verified_badge: true,
    enable_subscriber_badge: true,
    enable_reviewer_recommends: true,
    enable_attributes: true,
    enable_product_name: true,
    enable_replies: true,
    enable_images: false,
    enable_ratings: false,
    enable_share: true,
    enable_helpful_vote: true,
    enable_helpful_display: true,
    enable_report: true,
    enable_date: false,
    enable_third_party_source: true,
  },
  // Live's own values, trimmed to the ones that carry the look: square, black,
  // 16px base. The rest of its styles block is Polaris defaults restated.
  styles: {
    '--base-font-size': '16px',
    '--common-button-border-radius': '0px',
    '--common-button-border-width': '2px',
    '--common-button-text-transform': 'none',
    '--primary-button-bg-color': '#0E1311',
    '--primary-button-border-color': '#0E1311',
    '--primary-button-text-color': '#FFFFFF',
  },
};

/**
 * Echt · Reviews.
 *
 * Mounts the Reviews.io Polaris widget the first time the section comes near
 * the viewport, and never again. Nothing is requested from reviews.io before
 * that — roughly 200 KB of third-party JavaScript that the live theme loads on
 * every product page whether or not anyone scrolls to it.
 *
 * The preconnect goes in at mount time rather than in <head>, for the same
 * reason: a head preconnect costs a handshake on every PDP.
 *
 * 7 Sep 2026 — before any of that, one small JSON request decides whether the
 * reviews about to be shown are this product's. See #judge(): some catalogue
 * entries on this store were cloned from other products and serve their review
 * group, and both the widget and the count metafield believe it. That request
 * is the only thing this component asks for ahead of scroll, and it is the
 * same query the widget itself makes; the 200 KB of widget is still deferred.
 *
 * That answer is now the ONLY gate on both surfaces. The Liquid on either side
 * renders unconditionally and hidden — the section as `data-state="pending"`,
 * the star row with a `hidden` attribute — because every metafield this store
 * publishes about reviews proved wrong in both directions. This component
 * collapses or reveals, and rewrites the star row's numbers from the API
 * before showing it (#revealRatings).
 *
 * @extends Component
 */
class EchtReviews extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #mounted = false;
  /** @type {Promise<boolean | null> | null} true genuine, false not ours/none, null unknown. */
  #resolved = null;

  connectedCallback() {
    super.connectedCallback();

    // The Liquid renders `pending`, which is display:none — nothing is gated
    // server-side any more, so a page whose script never runs must show no
    // reviews surface at all. Moving to `checking` here is what brings the box
    // back (reserving its height, heading still held back by the stylesheet),
    // and it must happen BEFORE the observer below: a display:none element has
    // no box and would never intersect.
    this.dataset.state = 'checking';
    this.#resolved = this.#resolve();

    if (!('IntersectionObserver' in window)) {
      this.#maybeMount();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#maybeMount();
      },
      { rootMargin: '400px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * One request — the same `product/review?per_page=25` the widget is about to
   * make — and a verdict read off the SKUs of the reviews that come back.
   *
   * @returns {Promise<boolean | null>} true genuine, false none, null unknown.
   */
  async #resolve() {
    const store = this.dataset.store;
    const skus = this.dataset.skus;
    if (!store || !skus) return null;

    const result = await this.#stats(store, skus).catch(() => null);
    const genuine = this.#judge(result, skus);
    // Keep the existing SKU-family check on its original 25-review sample.
    // Only the published rating changes: never use the unfiltered headline
    // stats, even if the eligible-rating request fails.
    const visible = genuine === true ? await this.#visibleStats(store, skus).catch(() => null) : null;
    const verdict = visible?.count === 0 ? false : genuine;
    this.#apply(verdict, visible);
    return verdict;
  }

  /**
   * ROOT DIVERSITY. A product's reviews cluster on one or a few SKU families;
   * a cloned catalogue entry's do not.
   *
   * Measured over the 25-review sample on 7 Sep 2026:
   *   Nova (cloned from a Freedom Sportsbra entry) — 14 distinct roots
   *     (ESSENTIALSCOMBEDHOODIE 3, INVISBRA 3, ARISEINVISHALTERTANK 3,
   *      TERRYHOODIE 2, …), top root 12% of the sample.
   *   MODEPANT — 3 roots (33FLOWPANTS 15, 30FLOWPANTS 9, CONTRASTSHORT 1).
   *   FLARELEGGINGS — 3 roots, all FLARELEGGINGS*.
   *
   * Genuine when the count is non-zero AND any one of:
   *   - the sample spans at most MAX_ROOTS families (a merged family), or
   *   - one family owns MIN_DOMINANT_SHARE of it (a merged family with strays),
   *   - or a review's root is one of the product's OWN roots (the ordinary
   *     case, and the one that cannot be argued with).
   *
   * The third clause is why plain root-MATCHING is not the rule on its own:
   * `MODEPANT/BLACK/M` legitimately returns reviews whose SKUs read
   * `33FLOWPANTS/…`, a renamed family Reviews.io maintains and live depends on.
   * Requiring an own-root match would delete 104 real reviews, so it is one of
   * three ways to pass, never a requirement.
   *
   * @param {{ count: number, roots: string[] } | null} result
   * @param {string} skus this product's own SKUs, semicolon-joined.
   * @returns {boolean | null}
   */
  #judge(result, skus) {
    // The request failed or timed out. Unknown is not "none": fall through to
    // the pre-7-Sep behaviour rather than hiding real reviews on an outage.
    if (!result) return null;
    // A genuinely empty answer — nothing to host.
    if (!result.count) return false;

    const roots = result.roots.filter(Boolean);
    // Reviews came back but carry no usable SKUs. Nothing to judge on, so
    // fail open rather than invent a verdict.
    if (roots.length === 0) return true;

    const tally = new Map();
    for (const root of roots) tally.set(root, (tally.get(root) ?? 0) + 1);
    if (tally.size <= MAX_ROOTS) return true;
    if (Math.max(...tally.values()) / roots.length >= MIN_DOMINANT_SHARE) return true;

    const own = new Set(skus.split(';').map(rootOf).filter(Boolean));
    return roots.some((root) => own.has(root));
  }

  /**
   * Publishes the verdict: collapses this section when there is nothing to
   * host, reveals the PDP star row when there is, and fires a document event
   * so anything else on the page can react without this component knowing
   * about it.
   *
   * @param {boolean | null} genuine
   * @param {{ count: number, average: number, roots: string[] } | null} stats
   */
  #apply(genuine, stats) {
    // `none` collapses the section; anything else — including "unknown" —
    // lets it stand, because an outage must never blank real reviews.
    this.dataset.state = genuine === false ? 'none' : 'ready';

    // The star row is rendered by blocks/echt-rating.liquid at the top of the
    // buy column, far from here. It carries `data-echt-rating` purely as this
    // hook — no shared class, no id, so neither side depends on the other's
    // styling — and it arrives `hidden`.
    //
    // Note the asymmetry with the section above: an UNKNOWN verdict leaves the
    // stars hidden. Stars are an unattended claim about a product; the reviews
    // section is a container someone chose to scroll to. Showing stars we
    // cannot vouch for is the bug being fixed, so silence means no stars.
    if (genuine === true && stats) this.#revealRatings(stats);

    document.dispatchEvent(
      new CustomEvent('echt:reviews-resolved', {
        detail: { count: stats?.count ?? null, average: stats?.average ?? null, genuine },
      })
    );
  }

  /**
   * Rewrites every star row from the API's numbers, then unhides it.
   *
   * Nothing Liquid rendered is trusted once the API has answered: the
   * metafields behind those values are wrong in both directions — 1 on Nova
   * Trek products with no reviews, absent on mode-pants-black which has 104.
   * Everything the row states is overwritten:
   *   - `data-review-count`, the attribute the row publishes for verification;
   *   - `--echt-rating-percent`, the width of the filled star row;
   *   - `.echt-rating__count`, the visible "104 reviews" label, present only
   *     when the block's `show_count` is on (Jeremy, 7 Sep 2026: "displaying
   *     the quantity of reviews for products on PDP" — live prints it beside
   *     its stars). It arrives EMPTY from Liquid and is written nowhere else,
   *     because the metafield count is the wrong number in both directions;
   *   - the aria-label, the only place the numbers are ANNOUNCED. The count
   *     phrase is appended to it and the visible span is aria-hidden, so a
   *     screen reader hears the row once instead of twice.
   *
   * `data-min-rating` is live's merchandising rule, applied HERE and to the
   * API average rather than to the metafield rating, because the API average
   * is the score the shopper is being shown. A product Reviews.io scores 3.5
   * stays hidden, exactly as live's snippet leaves it hidden.
   *
   * @param {{ count: number, average: number }} stats
   */
  #revealRatings(stats) {
    for (const row of document.querySelectorAll('[data-echt-rating]')) {
      const min = Number(row.dataset.minRating) || 0;
      // No `hidden = true` in the else branch: the row is already hidden and
      // this method is the only thing that ever reveals one.
      if (!stats.count || stats.average < min) continue;

      row.dataset.reviewCount = String(stats.count);
      const valueEl = row.querySelector('[data-rating-value]');
      if (valueEl) valueEl.textContent = stats.average.toFixed(1);
      // The block draws five stars, so the fill is a percentage of five. The
      // API's average is on that scale whatever `scale_max` claimed.
      const percent = Math.max(0, Math.min(100, (stats.average / 5) * 100));
      row.querySelector('.echt-rating__stars')?.style.setProperty('--echt-rating-percent', `${percent.toFixed(1)}%`);

      // The visible count label, if this block was set to show one. The two
      // word forms come from the block (Liquid owns the translation); only
      // this side knows the number, so only this side can pick between them.
      // Plain `String()` rather than `toLocaleString()`: the digits are
      // compared verbatim against `data-review-count` and against the API in
      // tests/verify-review-count.mjs, and thousands separators would make
      // that comparison locale-dependent for no shopper-visible gain.
      const countEl = row.querySelector('.echt-rating__count');
      const countText = countEl
        ? `${stats.count} ${stats.count === 1 ? countEl.dataset.labelOne : countEl.dataset.labelOther}`
        : '';
      if (countEl) countEl.textContent = countText;

      // A translated sentence handed over with a placeholder, because Liquid's
      // `t` filter cannot be run from here.
      const template = row.dataset.ratingLabel;
      if (template) {
        const label = template.replace('__RATING__', stats.average.toFixed(1));
        // The count joins the label instead of being announced on its own —
        // the span is aria-hidden, so this is the only place it is spoken.
        row.setAttribute('aria-label', countText ? `${label}, ${countText}` : label);
      }

      row.hidden = false;
    }
  }

  /**
   * The headline stats plus the SKU root of every review in the sample — the
   * roots are the whole point, and they come free in the response the widget
   * was going to fetch anyway. `per_page=25` is what the share percentages
   * above were measured over; changing it changes the thresholds.
   *
   * @param {string} store
   * @param {string} skus semicolon-joined, exactly as the widget sends them.
   * @returns {Promise<{ count: number, average: number, roots: string[] }>}
   */
  async #stats(store, skus) {
    const url = `${API}?store=${encodeURIComponent(store)}&sku=${encodeURIComponent(skus)}&per_page=25`;
    // AbortSignal.timeout so a hung connection cannot leave the heading in its
    // `checking` state for the life of the page.
    const response = await fetch(url, { signal: AbortSignal.timeout(TIMEOUT_MS) });
    if (!response.ok) throw new Error(`reviews.io responded ${response.status}`);
    const payload = await response.json();
    // Measured shape is top-level `stats` / `reviews`; the `data` envelope is
    // tried too so a future shape change degrades to "unknown", not "none".
    const stats = payload?.stats ?? payload?.data?.stats ?? null;
    if (!stats || typeof stats.count !== 'number') throw new Error('reviews.io returned no stats');

    const list = payload?.reviews?.data ?? payload?.reviews ?? payload?.data?.reviews ?? [];
    // One root per review, never more: the tally below counts reviews, and a
    // row whose `sku` is an array of variant SKUs would otherwise vote several
    // times and inflate the diversity it is being measured for.
    const roots = (Array.isArray(list) ? list : []).map((review) =>
      rootOf(Array.isArray(review?.sku) ? review.sku[0] : review?.sku)
    );
    return { count: stats.count, average: Number(stats.average) || 0, roots };
  }

  /**
   * Polaris' rating buckets cover all available reviews across every page.
   * Weight only the buckets whose cards pass the same hide-below rule as
   * #curate. The response's headline average includes hidden reviews.
   * No search/sort/page filter is sent: these are the product's overall stats.
   *
   * @param {string} store
   * @param {string} skus
   * @returns {Promise<{ count: number, average: number }>}
   */
  async #visibleStats(store, skus) {
    const url = `${TIMELINE_API}?type=product_review&store=${encodeURIComponent(store)}&sku=${encodeURIComponent(skus)}&per_page=1&widget=polaris&lang=en`;
    const response = await fetch(url, { signal: AbortSignal.timeout(TIMEOUT_MS) });
    if (!response.ok) throw new Error(`reviews.io responded ${response.status}`);
    const payload = await response.json();
    const ratings = payload?.aggregations?.ratings;
    const buckets = ratings?.buckets;
    // A missing/truncated breakdown must not become a partial overall rating.
    if (!Array.isArray(buckets) || ratings.sum_other_doc_count > 0 || ratings.doc_count_error_upper_bound > 0) {
      throw new Error('reviews.io returned no complete rating breakdown');
    }
    const threshold = Number(this.dataset.hideBelow) || 0;
    let count = 0;
    let total = 0;
    let available = 0;
    const seen = new Set();
    for (const bucket of buckets) {
      const score = Number(bucket.key);
      const size = bucket.doc_count;
      if (!Number.isInteger(score) || score < 1 || score > 5 || !Number.isSafeInteger(size) || size < 0 || seen.has(score)) {
        throw new Error('reviews.io returned an invalid rating bucket');
      }
      seen.add(score);
      available += size;
      if (score < threshold) continue;
      count += size;
      total += score * size;
    }
    if (available !== payload?.stats?.results_count) {
      throw new Error('reviews.io returned an incomplete rating breakdown');
    }
    return { count, average: count ? total / count : 0 };
  }

  /**
   * Intersection reached. Wait for the verdict before spending 200 KB on a
   * widget that would render other people's reviews.
   */
  async #maybeMount() {
    this.#observer?.disconnect();
    let genuine = null;
    try {
      genuine = await this.#resolved;
    } catch {
      // #resolve() already swallows its own failures; this is belt and braces
      // so an unexpected throw still ends in the old behaviour.
    }
    if (genuine === false) return;
    this.#mount();
  }

  /**
   * Injects the preconnect hint and the widget script, both at this moment —
   * not at parse time. Guarded so a second intersection (or a second call from
   * the no-IntersectionObserver fallback) can never mount twice.
   */
  #mount() {
    if (this.#mounted) return;
    this.#mounted = true;
    this.#observer?.disconnect();

    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://widget.reviews.io';
    document.head.append(preconnect);

    const script = document.createElement('script');
    script.src = 'https://widget.reviews.io/polaris/build.js';
    script.async = true;
    script.addEventListener('load', () => this.#render());
    script.addEventListener('error', () => {
      // Reviews.io unreachable. Leave the reserved space and the noscript
      // link standing rather than collapsing the section under the footer
      // mid-scroll.
      this.dataset.state = 'failed';
    });
    document.head.append(script);
  }

  #render() {
    const Widget = window.ReviewsWidget;
    if (typeof Widget !== 'function') return;

    // A preset section can be added to a page more than once; the mount
    // point's id is section-scoped via data-mount-id so two instances never
    // both target the first #ReviewsWidget in the document. Falls back to
    // the historical bare id when the attribute is absent.
    const mountId = this.dataset.mountId || 'ReviewsWidget';
    const selector = `#${CSS.escape(mountId)}`;

    const sort = this.dataset.sort || 'date_desc';
    // `min_rating` is Reviews.io's documented filter and live sets it too, but
    // measured against the real API on 5 Sep 2026 it does NOT filter: 1- and
    // 3-star reviews still come back. It is sent for parity with live and in
    // case they fix it; #curate below is what actually does the hiding, which
    // is presumably why live carries its own DOM-level fix snippet as well.
    const minRating = Number(this.dataset.hideBelow) || undefined;

    new Widget(selector, {
      store: this.dataset.store,
      widget: 'polaris',
      options: {
        ...LIVE_OPTIONS,
        per_page: Number(this.dataset.perPage) || 15,
        default_sort: sort,
        product_review: {
          sku: this.dataset.skus,
          hide_if_no_results: false,
          min_rating: minRating,
        },
      },
    });

    this.#curate();
  }

  /**
   * The live theme's curation rule, ported from
   * `snippets/reviewsio-rating-snippet-fix.liquid`: reviews scoring below
   * `data-hide-below` are hidden from the list.
   *
   * Client-side because reviews.io injects the cards itself and its own
   * `min_rating` does not filter (see #render). Live drove this from a
   * throttled observer plus a 1500ms timeout hung off every pagination button;
   * one observer on the list covers both, since paginating replaces those
   * children.
   *
   * Set `hide_reviews_below` to 0 in the editor to publish every review.
   */
  #curate() {
    const threshold = Number(this.dataset.hideBelow);
    if (!threshold) return;

    const apply = () => {
      for (const item of this.querySelectorAll(REVIEW_ITEM)) {
        const stars = item.querySelector(REVIEW_STARS);
        const score = Number.parseFloat(stars?.getAttribute('title')?.match(/\d+(\.\d+)?/)?.[0] ?? '');
        if (Number.isNaN(score)) continue;
        // An inline `display`, not the `hidden` attribute: reviews.io's own
        // stylesheet sets `display` on these items, which beats the UA sheet's
        // `[hidden] { display: none }` and leaves the card on screen.
        item.style.display = score < threshold ? 'none' : '';
      }
    };

    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        apply();
      });
    }).observe(this, { childList: true, subtree: true });
    apply();
  }
}

if (!customElements.get('echt-reviews')) customElements.define('echt-reviews', EchtReviews);
